import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Đổ nội dung cho section "Recently viewed".
 *
 * Danh sách sản phẩm đã xem chỉ có trong localStorage của khách, server không biết —
 * nên phải nạp phía trình duyệt. Cách làm giống hệt `predictive-search.js:368`: dựng
 * URL tìm kiếm theo id rồi gọi Section Rendering API nạp lại chính section này trong
 * ngữ cảnh trang tìm kiếm, xong lấy phần lưới nhét vào chỗ cũ.
 */
class RecentlyViewedProducts extends HTMLElement {
  /**
   * @static @constant {number} Số sản phẩm hiện ra tối đa.
   *
   * Khớp với `columns` của section trong `templates/product.json` và
   * `templates/collection.json` (đang là 5). Đổi số này thì phải đổi cả `columns`
   * ở hai template lẫn `#MAX_PRODUCTS` (= số này + 1) trong
   * `recently-viewed-products.js`, không thì lưới thừa ô trống hoặc dư một món lẻ
   * tràn xuống hàng hai.
   */
  static MAX_VISIBLE = 5;

  connectedCallback() {
    // Chờ hết lượt render hiện tại rồi mới gọi mạng, đừng tranh băng thông với ảnh
    // sản phẩm ở nửa trên màn hình.
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.#load(), { timeout: 2000 });
    } else {
      setTimeout(() => this.#load(), 300);
    }
  }

  async #load() {
    // Nạp `recently-viewed-list`, KHÔNG nạp chính mình: `?section_id=` chỉ nhận tên
    // file section, mà section này nằm trong `templates/product.json` nên id của nó
    // trả về 404. Đã thử trên cả dev lẫn live.
    const listSection = this.dataset.listSection;
    if (!listSection) return this.#giveUp();

    // Trang sản phẩm ghi id của chính nó vào localStorage ngay khi tải, nên phải loại
    // ra — không thì sản phẩm đang xem hiện lại ngay bên dưới chính nó.
    //
    // Cắt còn MAX_VISIBLE SAU khi lọc, không phải trước. localStorage giữ 6 id
    // (MAX_VISIBLE + 1) đúng để bù cho món bị loại này, nên:
    //   • trang sản phẩm: 6 lưu − 1 đang xem = 5 hiện
    //   • trang collection: không loại gì, 6 lưu cắt còn 5 hiện
    // Hai trang cùng ra 5, khớp lưới 5 cột đặt trong product.json và collection.json.
    const excludeId = this.dataset.excludeId;
    const ids = RecentlyViewed.getProducts()
      .filter((id) => id !== excludeId)
      .slice(0, RecentlyViewedProducts.MAX_VISIBLE);

    if (ids.length === 0) return this.#giveUp();

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    let html;
    try {
      // `useCache: false` — danh sách đổi theo từng khách, cache lại là phát nhầm.
      html = await sectionRenderer.getSectionHTML(listSection, false, url);
    } catch {
      return this.#giveUp();
    }

    const incoming = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-rv-grid]');
    const target = this.querySelector('[data-rv-grid]');

    // Sản phẩm có thể đã bị xoá hoặc ẩn kể từ lần khách xem — lúc đó search trả về
    // rỗng và ta không có gì để hiện.
    if (!incoming || !target || incoming.children.length === 0) return this.#giveUp();

    target.replaceChildren(...incoming.children);
    this.hidden = false;
  }

  /** Gỡ hẳn khỏi trang: để lại thẻ rỗng là còn nguyên padding 48px trên dưới. */
  #giveUp() {
    this.remove();
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
